import { SearchResults, SelectedFilter } from "../Shared/scriptts/interfaces";
import { SearchPreference } from "../Shared/scriptts/search-preference";
import { getUserObject, isAuthenticated } from "../Shared/scriptts/session/sessionmain";
import { displayRemoteFilters } from './Templates/search-filter-remote';
import { displayOrganizationFilter } from './Templates/search-filter-organization';
import { displayPayFilter } from './Templates/search-filter-pay';
import { displaySeriesFilter } from './Templates/search-filter-series';
import { displayNoPartialFilters } from './Templates/search-filter-nopartial';
import { defaultRadius, minimumRadius, displayLocationFilter } from './Templates/search-filter-location';
import { displayFilterPills } from './Templates/search-filter-pills';
import { brandedSearch, params, aliasMapping, executeSearchUrl, displayResults, setParams, signinClick } from './search-main';
import { DefaultExistingParams, MergeParams } from "../Shared/scriptts/search-params";

export let preferences: { [id: string]: SearchPreference; };
export let userFilters: SelectedFilter[] = [];
export let filterCount = 0;

let preferenceToggle: boolean;
let preferenceCount = 0;

document.addEventListener('DOMContentLoaded', () => {   
    document.querySelector<HTMLButtonElement>('#filter-toggle-all')?.addEventListener('click', searchFiltersToggleAllClick);
    document.querySelector<HTMLButtonElement>('#search-preferences-toggle')?.addEventListener('click', searchPreferencesToggleClick);
    document.querySelector<HTMLButtonElement>('#search-preferences-signin')?.addEventListener('click', signinClick);
});

export function initUserPreferences() {
    if (brandedSearch) {
        return;
    }
    const userObject = getUserObject();
    if (!userObject.IsAuthenticated) {
        return;
    }    
    preferenceToggle = !(sessionStorage.getItem('profilePreferences') === 'false');
    preferences = {};

    userObject.HiringPaths.forEach(hp => {
        preferences[`hiring_path-${hp.Code}`] = new SearchPreference('HiringPath', hp.Code);
        if (preferenceToggle) {
            addToArrayParam('HiringPath', hp.Code);
        }
        else {
            removeFromArrayParam('HiringPath', hp.Code);
        }
    });

    if (userObject.Eligibility?.WantsRelocation === true) {
        preferences['relocation_assistance'] = new SearchPreference('RelocationIndicator', 'true');
        if (preferenceToggle) {
            params.RelocationIndicator = true;
        }
        else {
            params.RelocationIndicator = null;
        }
    }

    if (userObject.Eligibility?.WantsTelework === true) {
        preferences['telework_eligible'] = new SearchPreference('TeleworkIndicator', 'true');
        if (preferenceToggle) {
            params.TeleworkIndicator = true;
        }
        else {
            params.TeleworkIndicator = null;
        }
    }

    userObject.Preferences.PositionOfferings?.forEach(offeringCode => {
        if (offeringCode === '15669') {
            preferences['telework_eligible'] = new SearchPreference('TeleworkIndicator', 'true');
            if (preferenceToggle) {
                params.TeleworkIndicator = true;
            }
            else {
                params.TeleworkIndicator = null;
            }
        }
        else {
            preferences[`work_type-${offeringCode}`] = new SearchPreference('PositionOfferingTypeCode', offeringCode);
            if (preferenceToggle) {
                addToArrayParam('PositionOfferingTypeCode', offeringCode);
            }
            else {
                removeFromArrayParam('PositionOfferingTypeCode', offeringCode);
            }
        }
    });

    userObject.Preferences.PositionSchedules?.forEach(schedule => {
        preferences[`work_schedule-${schedule}`] = new SearchPreference('WorkSchedule', schedule);
        if (preferenceToggle) {
            addToArrayParam('WorkSchedule', schedule);
        }
        else {
            removeFromArrayParam('WorkSchedule', schedule);
        }
    });

    userObject.Preferences.TravelPercentage?.forEach(travelPct => {
        preferences[`travel_percentage-${travelPct}`] = new SearchPreference('TravelPercentage', travelPct);
        if (preferenceToggle) {
            addToArrayParam('TravelPercentage', travelPct);
        }
        else {
            removeFromArrayParam('TravelPercentage', travelPct);
        }
    });

    userObject.Preferences.DesiredLocations?.forEach(location => {
        preferences[`loc_${location.replace(/,| /g, '-')}`] = new SearchPreference('LocationName', location);
        if (preferenceToggle) {
            addToArrayParam('LocationName', location);
        }
        else {
            removeFromArrayParam('LocationName', location);
            if (params.LocationName.length === 0) {
                params.Radius = undefined;
            }
        }
    });

    preferenceCount = Object.keys(preferences).length;
}

export function initBrandedSearch() {
    if (!brandedSearch) {
        return;
    }
    const removeFiltersTextElem = document.querySelector<HTMLElement>('#remove-filters-text');

    setParams(MergeParams(DefaultExistingParams(structuredClone(brandedSearch)), params));

    if (!removeFiltersTextElem) {
        return;
    }
    removeFiltersTextElem.innerText = 'Remove filters you added';
}

export function displayFilters(data: SearchResults) {
    displayRemoteFilters(data);
    displayOrganizationFilter(data);
    displayPayFilter();
    displaySeriesFilter(data);
    displayNoPartialFilters(data);
    displayLocationFilter(data);
    displayFilterPills();
    const filterCountElem = document.querySelector<HTMLElement>('#filter-count');
    if (!filterCountElem) {
        return;
    }
    if (filterCount > 0) {
        filterCountElem.innerText = filterCount.toString();
        filterCountElem.classList.remove('hidden');
    }
    else {
        filterCountElem.innerText = '0';
        filterCountElem.classList.add('hidden');
    }
}

export function displayProfilePreferenceToggle() {
    if (isAuthenticated() === true) {
        document.querySelector<HTMLElement>('#search-prefs-noauth')?.classList.add('hidden');
        document.querySelector<HTMLElement>('#search-prefs-auth')?.classList.remove('hidden');
        const btn = document.querySelector<HTMLButtonElement>('#search-preferences-toggle');
        const msg = document.querySelector<HTMLElement>('#search-prefs-toggle-msg');
        if (btn === null) {
            console.error("Error: Can't find search preferences toggle button.");
            return;
        }
        preferenceToggle = (sessionStorage.getItem('profilePreferences') || 'true') === 'true';
        btn.title = `Turn ${preferenceToggle ? 'off' : 'on'} search preference filters from your profile.`;
        btn.innerHTML = `<svg class="w-7 h-7" role="presentation"><use xlink:href="/img/icons/sprite.svg#toggle_${preferenceToggle ? 'on' : 'off'}"></use></svg>`;
        if (msg !== null) {
            msg.innerHTML = preferenceToggle ?
                `You are now using <span id="search-preference-numfilters" class="font-bold">${preferenceCount}</span> filters from your profile for this search.` :
                'You are no longer using filters from your profile for this search.';
        }
        sessionStorage.setItem('profilePreferences', preferenceToggle.toString());
    }
    else {
        document.querySelector<HTMLElement>('#search-prefs-noauth')?.classList.remove('hidden');
        document.querySelector<HTMLElement>('#search-prefs-auth')?.classList.add('hidden');
    }
    if (brandedSearch) {
        document.querySelector<HTMLElement>('#search-all')?.classList.remove('hidden');
        document.querySelector<HTMLElement>('#search-prefs-toggle')?.classList.add('hidden');
    }
    else {
        document.querySelector<HTMLElement>('#search-all')?.classList.add('hidden');
        document.querySelector<HTMLElement>('#search-prefs-toggle')?.classList.remove('hidden');
    }
}

/**
 * Add a value to an array parameter in the params array
 * @param propertyName The name of the array property to add the value to
 * @param value The value to push to the parameter array
 */
export function addToArrayParam(propertyName: any, value: string) {
    if (!params.hasOwnProperty(propertyName) || !Array.isArray(params[propertyName])) {
        return;
    }
    if (params[propertyName]?.indexOf(value) === -1) {
        params[propertyName].push(value);
        params.Page = '1';

        if (propertyName === 'LocationName') {
            params.Radius = (!params.Radius) ? defaultRadius : params.Radius;
        }
    }
}

/**
 * Removes a from an array parameter value in the params array
 * @param propertyName The name of the array property remove the value from
 * @param value The value to remove from the parameter array
 */
export function removeFromArrayParam(propertyName: any, value: string) {
    if (!params.hasOwnProperty(propertyName) || !Array.isArray(params[propertyName])) {
        return;
    }
    const tempParams = params;
    const index = tempParams[propertyName]?.indexOf(value);

    if (index > -1) {
        tempParams[propertyName].splice(index, 1);
        tempParams.Page = '1';
    }
    setParams(tempParams);
}

export function removeParam(propertyName: any, value: string) {
    const tempParams = params;
    const param = params[propertyName];
    if (Array.isArray(param)) {
        removeFromArrayParam(propertyName, value);
    }
    else if (propertyName === 'Radius') {
        tempParams.Radius = minimumRadius;
    }
    else {
        tempParams[propertyName] = null;
    }

    if (propertyName === 'LocationName') {
        tempParams.Radius = undefined;
    }

    if (propertyName === 'GradeBucket' || propertyName === 'RemunerationMinimumAmount') {
        tempParams.GradeBucket = [];
        tempParams.RemunerationMinimumAmount = null;
        tempParams.RemunerationMaximumAmount = null;
        tempParams.GradeSearch = false;
    }

    tempParams.Page = '1';
    setParams(tempParams);
}

export function addParam(propertyName: any, value: string) {
    const param = params[propertyName];
    if (Array.isArray(param)) {
        addToArrayParam(propertyName, value);
        return;
    }

    params[propertyName] = value;
}

export function isBrandedSearchFilter(param: string, value: string) {
    const property = aliasMapping[param].JOAQueryName;
    if (!brandedSearch.hasOwnProperty(property)) {
        return false;
    }

    const filterValue = brandedSearch[property];

    if (Array.isArray(filterValue)) {
        return filterValue.indexOf(value) > -1;
    }
    else {
        return filterValue.toString().toLowerCase() == value.toLowerCase();
    }
}

export function isUserPreferenceFilter(filterType: string, value: string) {
    for (const pref in preferences) {
        if (preferences[pref].Type === filterType && preferences[pref].Value === value) {
            return true;
        }
    }

    return false;
}

export function resetFilterCount() {
    filterCount = 0;
}

export function setFilterCount(value: number) {
    filterCount = value;
}

export function getPreferencToggle(): boolean {
    return preferenceToggle;
}

function searchFiltersToggleAllClick(evt: Event) {
    const toggleButton = <HTMLButtonElement>(evt.target);
    let expand = toggleButton.getAttribute('data-expanded') === 'false';
    toggleButton.innerText = expand ? 'Collapse all' : 'Expand all';
    toggleButton.setAttribute('data-expanded', expand.toString());
    document.querySelectorAll<HTMLButtonElement>('[data-target^="search-filters_"]').forEach(elem => {
        if (elem.getAttribute('aria-expanded') === (!expand).toString()) {
            elem.dispatchEvent(new Event('click'));
        }
    });
}

export function toggleUserPreferences(toggleOn: boolean) {
    if (toggleOn === true) {
        sessionStorage.setItem('profilePreferences', 'true');
    }
    else {
        sessionStorage.setItem('profilePreferences', 'false');
    }
    displayProfilePreferenceToggle();
    initUserPreferences();
}

function searchPreferencesToggleClick(evt: Event) {
    toggleUserPreferences((sessionStorage.getItem('profilePreferences') || 'true') !== 'true');
    displayResults(executeSearchUrl, false, true);
}
